import React, { useEffect, useState, useRef } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { SortableItem } from "../../../../../components/SortableItemComponent";
import { FaSearch } from "react-icons/fa";

const SubmitActionDragDrop = ({
  initialUnassigned,
  assignedTasks,
  setAssignedTasks,
  setUnAssignedTasks,
  setSearchQuery,
  onSubmit,
  gotData,
  handleSearchAvailableChange,
  handleSearchSelectedChange,
}) => {
  const [unassignedTasks, setUnassignedTasks] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const wasDragged = useRef(false);

  useEffect(() => {
    if (gotData) {
      setUnassignedTasks(initialUnassigned);
      setAssignedTasks(assignedTasks);
    }
  }, [gotData, initialUnassigned]);

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragStart = (event) => {
    wasDragged.current = false;
    setActiveId(event.active.id);
  };

  const handleDragMove = () => {
    wasDragged.current = true;
  };

  const handleDragEnd = (event) => {
    if (!wasDragged.current) return;

    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const draggedTask = unassignedTasks.find((task) => task.id === active.id);

    if (draggedTask && !draggedTask.displayName.includes("rpt_")) {
      alert("⚠️ Warning: This is not a Reporting Procedure.");
      return;
    }

    const isActiveInUnassigned = unassignedTasks.some(
      (task) => task.id === active.id
    );
    const isActiveInAssigned = assignedTasks.some(
      (task) => task.id === active.id
    );

    let updatedAssignedTasks = [...assignedTasks];
    let updatedUnassignedTasks = [...unassignedTasks];
    let newGotFormData = gotData;
    let movedProcedureName = "";

    if (isActiveInUnassigned && !isActiveInAssigned) {
      const movedProcedure = unassignedTasks.find(
        (task) => task.id === active.id
      );
      updatedUnassignedTasks = unassignedTasks.filter(
        (task) => task.id !== active.id
      );
      updatedAssignedTasks = [...assignedTasks, movedProcedure];
      movedProcedureName = movedProcedure.displayName;
      newGotFormData = true;
    } else if (isActiveInAssigned && !isActiveInUnassigned) {
      const movedProcedure = assignedTasks.find(
        (task) => task.id === active.id
      );
      updatedAssignedTasks = assignedTasks.filter(
        (task) => task.id !== active.id
      );
      updatedUnassignedTasks = [...unassignedTasks, movedProcedure];
      movedProcedureName = movedProcedure.displayName;
      newGotFormData = false;
    }

    setAssignedTasks(updatedAssignedTasks);
    setUnAssignedTasks(updatedUnassignedTasks);
    setUnassignedTasks(updatedUnassignedTasks);

    if (movedProcedureName) {
      onSubmit(newGotFormData, movedProcedureName);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
    >
      <div style={{ display: "flex", gap: "2rem" }}>
        {/* Unassigned Tasks List */}
        <div
          style={{
            border: "1px solid #ccc",
            borderRadius: "5px",
            padding: "1rem",
            width: "500px",
            maxHeight: "100vh",
            overflowY: "auto",
          }}
        >
          <h4>List of All Procedures</h4>
          <hr style={{ height: "2px", marginTop: "-1px", background: "#e0e0e0" }} />
          <div className="relative w-full fButton">
            <FaSearch className="absolute left-3 top-[1.8rem] transform -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              placeholder="Search..."
              className="w-full pl-10 p-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              onChange={handleSearchAvailableChange}
            />
          </div>
          <SortableContext
            items={unassignedTasks.map((task) => task.id)}
            strategy={verticalListSortingStrategy}
          >
            {unassignedTasks.map((task) => (
              <SortableItem key={task.id} id={task.id} displayName={task.displayName} />
            ))}
          </SortableContext>
        </div>

        {/* Assigned Tasks List */}
        <div
          style={{
            border: "1px solid #ccc",
            borderRadius: "5px",
            padding: "1rem",
            width: "500px",
            maxHeight: "100vh",
            overflowY: "auto",
          }}
        >
          <h4>Selected Procedures for Report</h4>
          <hr style={{ height: "2px", marginTop: "-1px", background: "#e0e0e0" }} />
          <div className="relative w-full firstButton">
            <FaSearch className="absolute left-3 top-[1.8rem] transform -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              placeholder="Search..."
              className="w-full pl-10 p-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              onChange={handleSearchSelectedChange}
            />
          </div>
          <SortableContext
            items={assignedTasks.map((task) => task.id)}
            strategy={verticalListSortingStrategy}
          >
            {assignedTasks
              .filter((task) => task.id !== "-1")
              .map((task) => (
                <SortableItem key={task.id} id={task.id} displayName={task.displayName} />
              ))}
          </SortableContext>
        </div>
      </div>

      {/* Drag Overlay */}
      <DragOverlay>
        {activeId ? (
          <SortableItem
            id={activeId}
            displayName={
              unassignedTasks.find((task) => task.id === activeId)?.displayName ||
              assignedTasks.find((task) => task.id === activeId)?.displayName
            }
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default SubmitActionDragDrop;
